/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import AdminContext from '../contexts/AdminContext';
import { Filter } from '../../config/interfaces';

interface IGetUsersPayload {
  accessLevel: string;
  accessLevelId: string;
  hcaId: string;
  name: string;
  page: number;
  role: string;
}

/**
 * Redux slice for managing admin-related state using Redux Toolkit's `createSlice`.
 *
 * This slice handles actions related to user and unit management in the admin context, including:
 * - Managing admin search terms, filters, and selected facility
 * - Fetching and storing encounter data
 * - Managing loading states for all asynchronous admin operations
 *
 * @property {object} initialState - The initial state for the admin context.
 * @property {Function} setUsersPage - Sets the current page for user pagination.
 * @property {Function} setAdminUserTab - Sets the active admin user tab.
 * @property {Function} setAdminUserFilter - Sets the filter for admin users.
 * @property {Function} clearAdminUserFilter - Clears the admin user filter.
 * @property {Function} setAdminSearchTerm - Sets the search term for admin users.
 * @property {Function} setAdminFacility - Sets the selected facility for admin operations.
 * @property {Function} getCSVToExport - Initiates CSV export and sets loading state.
 * @property {Function} getCSVToExportSuccess - Handles successful CSV export, stores data and resets loading state.
 * @property {Function} getCSVToExportFailure - Handles failed CSV export, resets loading state.
 * @property {Function} clearCsvToExport - Clears exported CSV data and resets loading state.
 *
 * @example
 * dispatch(setAdminUserFilter({ role: 'patientRounder' }));
 * dispatch(getCSVToExport());
 *
 * @returns {object} The admin slice reducer and actions.
 */
const adminSlice = createSlice({
  name: 'admin',
  initialState: AdminContext,
  reducers: {
    setUsersPage: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        currentUsersPage: action.payload
      };
    },
    setAdminUserTab: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        adminUserTab: action.payload,
        currentUsersPage: 1
      };
    },
    setAdminUserFilter: (state, action: PayloadAction<Filter>) => {
      return {
        ...state,
        adminUserFilter: action.payload,
        currentUsersPage: 1
      };
    },
    clearAdminUserFilter: state => {
      return {
        ...state,
        adminUserFilter: {} as Filter,
        currentUsersPage: 1
      };
    },
    setAdminSearchTerm: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        adminSearchTerm: action.payload,
        currentUsersPage: 1
      };
    },
    setAdminFacility: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        selectedFacility: action.payload
      };
    },
    getCSVToExport: state => {
      return {
        ...state,
        isLoadingCsvExport: true
      };
    },
    getCSVToExportSuccess: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        dataToCsvExport: action.payload,
        isLoadingCsvExport: false
      };
    },
    getDataToDownloadSuccess: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        dataToDownload: action.payload,
        isLoadingCsvExport: false
      };
    },
    clearDataToDownload: state => {
      return {
        ...state,
        dataToDownload: [],
        isLoadingCsvExport: false
      };
    },
    getCSVToExportFailure: state => {
      return {
        ...state,
        isLoadingCsvExport: false
      };
    },
    clearCsvToExport: state => {
      return {
        ...state,
        dataToCsvExport: [],
        dataToDownload: [],
        isLoadingCsvExport: false
      };
    }
  }
});

export const {
  setUsersPage,
  setAdminUserTab,
  setAdminUserFilter,
  clearAdminUserFilter,
  setAdminSearchTerm,
  setAdminFacility,
  getCSVToExport,
  getCSVToExportSuccess,
  getDataToDownloadSuccess,
  clearDataToDownload,
  getCSVToExportFailure,
  clearCsvToExport
} = adminSlice.actions;

export default adminSlice.reducer;
